// Web-only fetch fallback to api-ru.agapella.io.
//
// Primary API host is api.agapella.io / 204.168.236.62. This file used to
// open with «fast from RU per our probe, so we don't pre-switch» — that is
// no longer true and has not been since CORE-723: from Russia a response
// from that address dies around 10-15 KB and the connection it died on
// carries nothing afterwards. Pre-switching is the job of apiEndpoint.ts
// (RU-first); this file is what happens when RU-first did NOT engage —
// the visitor is in Russia but carries no RU signal at all.
//
// If the network actually fails (TypeError: Failed to fetch — DNS, TCP,
// TLS, or hard transport) OR a timeout fires — our own PRIMARY_STALL_MS
// ceiling on the first attempt, or fetchWithTimeout's 60 s ceiling from
// api.ts (DOMException with name='TimeoutError') — we retry once to
// api-ru.agapella.io (our nginx in Moscow, proxies the same upstream).
// After 3 consecutive primary failures we set a sticky breaker that swaps
// the URL up-front until page reload, so the user doesn't pay the failed-
// fetch latency on every subsequent request.
//
// User-initiated AbortError (a plain abort without a TimeoutError reason —
// e.g. caller cancelled their own request) is never retried — it's
// intentional cancellation, not a failure to surface to the user.
//
// SignalR uses its own fetch internally; the hub URL switch is handled by
// the same breaker via the helper exported below.
//
// Native (iOS / Android) does not go through this path. It is shaped from
// Russia all the same (CORE-723), and has its own copy of these rules in
// nativeApiFallback.ts (CORE-1528).

import { Platform } from 'react-native';
import { NET_RESILIENCE_ENABLED, resilientFetch, setRuMode, isRuMode, clearRuMode, isIdempotent as isIdempotentMethod } from './netResilience';
import { trackApiRuFallback } from './netTelemetry';
import { ABSOLUTE_API_URL } from './apiUrl';
import { hasIdempotencyKey } from './idempotencyKey';

const PRIMARY = 'https://api.agapella.io';
const FALLBACK = 'https://api-ru.agapella.io';

// CORE-1066 — origin, на который ЭТА страница реально шлёт запросы.
//
// Берётся из `apiUrl`, а НЕ из `process.env.EXPO_PUBLIC_API_URL`: базовый адрес
// выбирается в рантайме по hostname браузера. На RU-зеркале
// (`app.rudev.tmless.org`) это `api.rudev.tmless.org`, тогда как переменная
// сборки говорит `api.agdev.tmless.org` — по ней ветка не сработала бы ровно в
// том случае, ради которого её и заводили (Sentry REACT-NATIVE-3M пришёл именно
// с `api.rudev.tmless.org`).
//
// Всё, что ниже про RU-эдж (sticky, переписывание URL на FALLBACK), намеренно
// остаётся продовым: у дева нет RU-двойника, переписывать некуда. Но ОДНА вещь
// нужна везде — повтор при транспортном отказе, см. retryOnceOnTransportFailure.
const CONFIGURED = (ABSOLUTE_API_URL || PRIMARY).replace(/\/+$/, '');

const BREAKER_THRESHOLD = 3;
const STORAGE_KEY = 'apiFallback:stickyAt';
const STICKY_TTL_MS = 30 * 60 * 1000; // 30 min

// Ceiling for the fallback retry when the primary tripped on a timeout
// (not a TypeError). Same as the per-attempt fetchWithTimeout ceiling in
// api.ts, so worst-case the user waits ~60 s primary + ~60 s fallback.
const FALLBACK_TIMEOUT_MS = 60_000;

/**
 * CORE-1398 — потолок ПЕРВОЙ попытки на primary.
 *
 * Шейпинг из РФ не бросает ошибку — он ВЕШАЕТ запрос. Поэтому ветка
 * `catch (TypeError)` ниже не срабатывает вовсе, и всё держалось на потолке
 * `fetchWithTimeout` из api.ts, а он 60 с: человек ждал минуту, прежде чем
 * начиналась хоть какая-то попытка на api-ru, и до двух минут суммарно.
 * Замер 12.09.2026 с московской коробки, `GET /api/practices`:
 *
 *     api.agapella.io     200 за 30 с, 15 942 Б  -> оборвался
 *     api.agapella.io     таймаут, 0 байт (следующий коннект уже мёртв)
 *     api-ru.agapella.io  200 за 0.39 с, 27 111 Б (3 из 3)
 *
 * Здоровый ответ приходит за 0.3–0.4 с, так что 8 с — это ×20 запаса: живой
 * эндпоинт так не отвечает, а зависший виден почти сразу. Платит этот потолок
 * ТОЛЬКО первый запрос вкладки: его срыв взводит sticky-брейкер, и всё
 * остальное уходит на api-ru без задержки.
 *
 * Потолок меряет время ДО ЗАГОЛОВКОВ (таймер снимается, когда promise
 * fetch'а разрешился), поэтому длинное тело под него не попадает.
 *
 * Сюда НЕ попадает `isContentCreate`: неидемпотентный POST, для которого
 * повтор после таймаута означает дубль практики (CORE-315). У него
 * по-прежнему 60 с и никакого фолбэка по таймауту.
 */
const PRIMARY_STALL_MS = 8_000;

let consecutiveFailures = 0;
let sticky = false;

function loadSticky(): boolean {
  try {
    const raw = window.localStorage?.getItem(STORAGE_KEY);
    if (!raw) return false;
    const ts = parseInt(raw, 10);
    if (Number.isNaN(ts)) return false;
    return Date.now() - ts < STICKY_TTL_MS;
  } catch {
    return false;
  }
}

/**
 * CORE-1323 — почему брейкер защёлкнулся. Момент «весь трафик этой вкладки
 * ушёл на api-ru» до сих пор был невидим снаружи, и по телеметрии нельзя было
 * отличить «фолбэк не понадобился» от «фолбэк молча работает всю сессию».
 */
type StickyReason = 'breaker' | 'gateway_5xx' | 'cross_layer' | 'resilience' | 'restored' | 'stall';

function setSticky(reason: StickyReason): void {
  // Эмитим только ПЕРЕХОД: markApiRuSticky() зовётся из хаба сколько угодно раз,
  // а событие должно значить «вкладка ушла на api-ru», а не «ещё один вызов».
  if (!sticky) trackApiRuFallback({ phase: 'sticky', decisionSource: reason });
  sticky = true;
  // CORE-449 — also flip the shared cross-layer sticky so SignalR + Stream
  // pre-route to their RU hosts once REST has confirmed the block.
  setRuMode();
  try {
    window.localStorage?.setItem(STORAGE_KEY, String(Date.now()));
  } catch {
    // localStorage unavailable (private mode etc.) — sticky still holds
    // in memory for this tab.
  }
}

function clearSticky(): void {
  sticky = false;
  try {
    window.localStorage?.removeItem(STORAGE_KEY);
  } catch {
    // ignore
  }
}

function rewriteUrl(url: string): string {
  return url.replace(PRIMARY, FALLBACK);
}

/**
 * CORE-1323 — путь запроса для телеметрии, без query-строки: она носит
 * пользовательские фильтры и токены, а для вопроса «какой вызов сорвался»
 * достаточно pathname.
 */
function requestPath(url: string): string {
  try {
    return new URL(url).pathname;
  } catch {
    return url.split('?')[0];
  }
}

/**
 * Returns the URL we should actually fetch right now — either the original
 * or the api-ru fallback if the breaker is sticky. Used by callers that
 * build URLs synchronously (e.g. SignalR hub URL resolution).
 */
export function resolveApiUrl(url: string): string {
  if (!url.startsWith(PRIMARY)) return url;
  // CORE-449 — also honour the shared cross-layer sticky, so a block first
  // seen by Stream/SignalR pre-routes REST + hub URLs to api-ru too.
  return (sticky || isRuMode()) ? rewriteUrl(url) : url;
}

/**
 * Unconditionally rewrite a primary-host URL to api-ru — for an EXPLICIT
 * fallback retry (e.g. a SignalR hub whose direct WS hung) before the sticky
 * breaker has tripped. A non-primary URL (dev host) is returned unchanged, so
 * callers can compare `ruFallbackUrl(u) === u` to detect "no RU edge here".
 */
export function ruFallbackUrl(url: string): string {
  return url.startsWith(PRIMARY) ? rewriteUrl(url) : url;
}

/**
 * Trip the api-ru breaker from a NON-fetch failure. The window.fetch patch
 * only trips `sticky` after REST calls fail, but a live-chat / hub WebSocket
 * that hangs (the RU block signature) never surfaces as a fetch error. When a
 * hub fell back to api-ru we call this so subsequent REST + hub URLs resolve
 * straight to api-ru for the 30-min sticky window instead of re-paying the
 * connect-timeout each time.
 */
/** CORE-1884 — is the api-ru breaker on right now (for the entry-attempt snapshot). */
export function isApiFallbackSticky(): boolean {
  if (Platform.OS !== 'web') return false;
  return sticky || loadSticky();
}

export function markApiRuSticky(): void {
  if (Platform.OS !== 'web') return;
  setSticky('cross_layer');
}

/**
 * CORE-1066 — один повтор того же адреса при ЧИСТО транспортном отказе.
 *
 * Зачем. Дев-RU-эдж проксирует всё на DE-релей, который из РФ периодически не
 * отвечает на коннект: 27 отказов за 13 часов. Пользователь ждал 15 секунд
 * (`proxy_connect_timeout` того вхоста) и получал `TypeError: Failed to fetch`
 * без единой попытки повтора — так сорвалось создание Live-сессии
 * (Sentry REACT-NATIVE-3M). Продовый путь ниже такие отказы переживает, потому
 * что у него есть RU-фолбэк; у дев-сборки не было ничего.
 *
 * Повторяем ТОЛЬКО `TypeError`. Таймаут (`TimeoutError`) и отмену вызывающим
 * (`AbortError`) не трогаем: первый значит, что сервер, возможно, работает над
 * запросом, вторая — осознанное решение вызывающего.
 *
 * CORE-1905 — повторяем только идемпотентные методы (GET/HEAD/OPTIONS).
 * Первая версия повторяла и POST: считалось, что `TypeError` значит «запрос
 * не дошёл». В браузере это не так — `TypeError` («Load failed» в Safari)
 * получает и ответ без CORS-заголовков: 502/503/504, которые сами отдают
 * nginx-proxy дева на передеплое бэкенда, Vercel-прокси RU-эджа или эдж.
 * Такой ответ мог прийти и после того, как бэкенд запрос уже выполнил
 * (таймаут функции Vercel), и тогда слепой повтор POST `/api/watch-parties`
 * создаёт вторую сессию. Причина, ради которой POST повторяли (15-секундный
 * коннект эджа к DE-релею), снята на самом эдже: `/api/` идёт через Vercel с
 * `proxy_connect_timeout 700ms` и `proxy_next_upstream`, а nginx переотправляет
 * и POST, пока тот не ушёл в upstream. Неидемпотентный запрос получает ошибку,
 * и решение о повторе остаётся за человеком (форма снимает блокировку кнопки).
 *
 * CORE-1910 — исключение: запрос с `Idempotency-Key`. Бэкенд на повтор с тем же
 * ключом отдаёт ответ первого запроса (а пока тот выполняется — 409), так что
 * дубля нет, и такой POST снова повторяется. Ключ едет в тех же `init`/`Request`.
 */
function requestMethod(input: any, init: RequestInit | undefined): string {
  return (init?.method
    || (input && typeof input === 'object' && 'method' in input ? (input as Request).method : '')
    || 'GET').toUpperCase();
}

async function retryOnceOnTransportFailure(
  originalFetch: typeof fetch,
  input: any,
  init: RequestInit | undefined,
): Promise<Response> {
  try {
    return await originalFetch(input, init);
  } catch (err) {
    if (!(err instanceof TypeError)) throw err;
    if (!isIdempotentMethod(requestMethod(input, init)) && !hasIdempotencyKey(input, init)) throw err;
    try {
      // Строку можно переиспользовать как есть; у Request тело первой попыткой
      // уже вычитано, поэтому пересобираем его из оригинала.
      const retryInput = typeof input === 'string' || input instanceof URL
        ? input
        : new Request((input as Request).url, input as Request);
      return await originalFetch(retryInput, init);
    } catch {
      // Повтор не помог — отдаём исходную ошибку: она информативнее.
      throw err;
    }
  }
}

export function installApiFallback(): void {
  if (Platform.OS !== 'web') return;
  if (typeof window === 'undefined' || typeof window.fetch !== 'function') return;
  if ((window as any).__apiFallbackInstalled) return;
  (window as any).__apiFallbackInstalled = true;

  sticky = loadSticky();
  // CORE-1323 — перезагрузка страницы с ещё живым sticky-окном: вкладка стартует
  // сразу на api-ru, ни одного отказа в этой сессии не будет, и без этой строки
  // такая сессия выглядит в телеметрии как «фолбэк ни разу не понадобился».
  if (sticky) trackApiRuFallback({ phase: 'sticky', decisionSource: 'restored' });

  const originalFetch = window.fetch.bind(window);

  window.fetch = async function patchedFetch(input: any, init?: RequestInit): Promise<Response> {
    const requestUrl =
      typeof input === 'string'
        ? input
        : input instanceof URL
          ? input.toString()
          : input && typeof input === 'object' && 'url' in input
            ? (input as Request).url
            : String(input);

    // Only intercept calls to our primary backend.
    if (!requestUrl.startsWith(PRIMARY)) {
      // CORE-1066 — не прод: RU-эджа нет и переписывать URL некуда, но один
      // повтор при транспортном отказе нужен и здесь. Всё остальное ниже
      // (sticky-брейкер, ladder, подмена хоста) не трогаем.
      if (CONFIGURED && CONFIGURED !== PRIMARY && requestUrl.startsWith(CONFIGURED)) {
        return retryOnceOnTransportFailure(originalFetch, input, init);
      }
      return originalFetch(input, init);
    }

    // CORE-449 — progressive-ladder + shared-RU-sticky path (flagged). When
    // enabled it supersedes the legacy consecutive-failure breaker below:
    // per-attempt timeouts escalate (1s→2s→4s for short requests), RU is
    // tried for sure once the primary ladder is spent, and the first RU
    // success flips both the legacy breaker and the shared ruMode.
    if (NET_RESILIENCE_ENABLED) {
      return resilientFetch({
        originalFetch,
        input,
        init,
        url: requestUrl,
        toRu: rewriteUrl,
        onRuSuccess: () => setSticky('resilience'),
      });
    }

    const method = (
      input && typeof input === 'object' && 'method' in input
        ? (input as Request).method
        : init?.method || 'GET'
    ).toUpperCase();

    // CORE-315 — the practice-create POST is non-idempotent: if it timed out, the
    // server may have already received it and be processing, so a transparent
    // retry (or sticky reroute to api-ru) would create a DUPLICATE practice. Flag
    // it so the catch below never retries a create on timeout and never lets a
    // create trip the breaker. (A pure transport TypeError — never reached the
    // server — is still safe to fall back, so that case is left alone.)
    const isContentCreate = method === 'POST' && /\/api\/master\/content\/videos(?:\?|$)/.test(requestUrl);

    // CORE-1910 — можно ли переотправить запрос (на api-ru), не рискуя дублем.
    // Идемпотентный метод — да. POST/PUT/PATCH/DELETE — только с
    // Idempotency-Key: `TypeError` в браузере бывает и на ответ без CORS уже
    // выполненного запроса (CORE-1905), а таймаут — на запросе, который сервер
    // ещё выполняет (CORE-315). Без ключа такой запрос не повторяется и не
    // обрывается нашим 8-секундным потолком — ровно как create практики.
    const replaySafe = isIdempotentMethod(method) || hasIdempotencyKey(input, init);

    // Sticky breaker — go straight to fallback.
    if (sticky) {
      const fallbackInput = typeof input === 'string' ? rewriteUrl(input) : input;
      const finalInit =
        typeof input === 'string'
          ? init
          : { ...(init || {}), };
      if (typeof input !== 'string') {
        // Need to rebuild the Request against the new URL.
        const req = input as Request;
        return originalFetch(new Request(rewriteUrl(req.url), req), finalInit);
      }
      return originalFetch(fallbackInput, finalInit);
    }

    // CORE-604 — a GET/HEAD is idempotent, so retrying it is always safe.
    const isIdempotent = method === 'GET' || method === 'HEAD';

    // CORE-1398 — взвелся ли НАШ потолок первой попытки (а не таймаут
    // вызывающего и не его же отмена). Читается в catch: по нему ветка
    // повтора работает даже там, где движок потерял reason у abort'а, и по
    // нему же `errorKind: 'stall'` отличается в телеметрии от честных 60 с.
    let stalled = false;

    /**
     * CORE-1398 — первая попытка на primary под собственным потолком.
     * Сигнал передаём через `init`, а не пересобирая Request: по спеке
     * `init.signal` перекрывает сигнал Request'а, и тело остаётся непрочитанным
     * — иначе повтор на api-ru ниже упал бы на «body already used».
     */
    const fetchPrimary = (): Promise<Response> => {
      if (isContentCreate || !replaySafe) return originalFetch(input, init);

      const ctrl = new AbortController();
      const caller: AbortSignal | null =
        init?.signal
        ?? (input && typeof input === 'object' && 'signal' in input ? (input as Request).signal : null)
        ?? null;
      const onCallerAbort = () => ctrl.abort((caller as any)?.reason);
      if (caller) {
        if (caller.aborted) ctrl.abort((caller as any).reason);
        else caller.addEventListener('abort', onCallerAbort);
      }
      const timer = setTimeout(() => {
        stalled = true;
        ctrl.abort(new DOMException(
          `Ответ от api.agapella.io не пришёл за ${Math.round(PRIMARY_STALL_MS / 1000)} с — пробуем api-ru.`,
          'TimeoutError',
        ));
      }, PRIMARY_STALL_MS);

      return originalFetch(input, { ...(init || {}), signal: ctrl.signal }).finally(() => {
        clearTimeout(timer);
        if (caller) caller.removeEventListener('abort', onCallerAbort);
      });
    };

    try {
      const res = await fetchPrimary();
      // CORE-604 — a GATEWAY 5xx (502/503/504) on an idempotent request to the
      // primary is the RU-edge / ISP-shaping signature (the request never reached
      // a healthy origin), so retry once to api-ru (same upstream) instead of
      // surfacing an empty block. An application 5xx (500) is a real backend error
      // — don't mask it and don't burn the fallback budget on it.
      const isGatewayError = res.status === 502 || res.status === 503 || res.status === 504;
      if (isIdempotent && isGatewayError && !isContentCreate) {
        try {
          const ruRes = typeof input === 'string'
            ? await originalFetch(rewriteUrl(input), init)
            : await originalFetch(new Request(rewriteUrl((input as Request).url), input as Request), init);
          trackApiRuFallback({ httpStatus: ruRes.status, primaryStatus: res.status });
          if (ruRes.ok) {
            // Confirmed the block is RU-shaping, not a backend outage — pin
            // subsequent calls to api-ru for the sticky window.
            consecutiveFailures = 0;
            setSticky('gateway_5xx');
            return ruRes;
          }
          // api-ru no better — fall through and surface the original response.
        } catch {
          // api-ru retry failed outright — surface the original gateway response.
          trackApiRuFallback({ httpStatus: null, primaryStatus: res.status });
        }
      }
      // 5xx still counts as primary working at the network level — don't
      // burn the budget on backend errors.
      if (res.ok || (res.status >= 200 && res.status < 600)) {
        consecutiveFailures = 0;
      }
      return res;
    } catch (err) {
      // Retry on TypeError (DNS / TCP / TLS / hard transport failures)
      // OR on our own fetchWithTimeout firing — DOMException whose name
      // is 'TimeoutError'. A plain AbortError without that name means
      // the caller cancelled their own request and we must not retry.
      const isNetwork = err instanceof TypeError;
      // CORE-1398 — `stalled` первым: часть движков теряет reason у abort'а и
      // отдаёт безымянный AbortError, а тогда наш собственный потолок был бы
      // неотличим от отмены вызывающим и повтор не случился бы вовсе.
      const isTimeout =
        stalled
        || (typeof DOMException !== 'undefined'
          && err instanceof DOMException
          && err.name === 'TimeoutError');
      if (!isNetwork && !isTimeout) throw err;

      // CORE-315 — a content-create that TIMED OUT is ambiguous (the server may
      // have received the POST and still be working). Retrying it — or letting it
      // trip the breaker that pins every later call to api-ru — risks a duplicate
      // practice, so surface the timeout to the caller instead. A transport
      // TypeError never reached the server, so that still falls back below.
      if (isContentCreate && isTimeout) throw err;

      consecutiveFailures += 1;
      if (consecutiveFailures >= BREAKER_THRESHOLD) {
        setSticky('breaker');
      }

      // CORE-1910 — отказ засчитан брейкеру (это всё ещё сигнал «primary не
      // отвечает»), но сам запрос без ключа не переотправляем.
      if (!replaySafe) throw err;

      // For a timeout retry the caller's AbortSignal is already aborted,
      // so reusing the same `init` would have the fallback `originalFetch`
      // reject immediately. Strip the caller signal and install a fresh
      // 60 s ceiling for the fallback attempt so the total wait stays
      // bounded (worst case ~60 s primary + ~60 s fallback).
      let retryInit: RequestInit | undefined = init;
      let retryTimer: ReturnType<typeof setTimeout> | null = null;
      if (isTimeout) {
        const retryCtrl = new AbortController();
        retryTimer = setTimeout(() => retryCtrl.abort(new DOMException(
          `Запрос к api-ru тоже занял больше ${Math.round(FALLBACK_TIMEOUT_MS / 1000)} с.`,
          'TimeoutError',
        )), FALLBACK_TIMEOUT_MS);
        retryInit = { ...(init || {}), signal: retryCtrl.signal };
      }

      // CORE-1323 — до этой правки основной путь фолбэка не логировал НИЧЕГО, и
      // на вопрос «а api-ru вообще пробовали?» ответить было нечем: 0 событий
      // api_ru_fallback означало «не видно», а не «не было». Пишем попытку ДО
      // запроса — ровно эти отказы висят по 60 с, и пользователь закрывает
      // вкладку, не дождавшись исхода.
      const errorKind = isNetwork ? 'network' : (stalled ? 'stall' : 'timeout');
      const path = requestPath(requestUrl);
      trackApiRuFallback({ phase: 'attempt', errorKind, method, path });

      try {
        const ruRes = typeof input === 'string'
          ? await originalFetch(rewriteUrl(input), retryInit)
          // When the caller used a Request object, the underlying
          // AbortSignal lives on the Request itself. For a timeout retry
          // we explicitly pass retryInit so the fresh signal wins over the
          // Request's aborted one (per fetch spec, init.signal overrides).
          : await originalFetch(new Request(rewriteUrl((input as Request).url), input as Request), retryInit);
        trackApiRuFallback({ phase: 'outcome', errorKind, method, path, httpStatus: ruRes.status });
        // CORE-1398 — primary завис, api-ru ответил: это доказанный шейпинг, а
        // не разовый сбой, и ждать ещё двух отказов до BREAKER_THRESHOLD незачем.
        // Без этой ветки потолок в 8 с платил бы каждый из первых трёх запросов.
        if (stalled && ruRes.ok) {
          consecutiveFailures = 0;
          setSticky('stall');
        }
        return ruRes;
      } catch {
        // Both failed — surface the original error so observability tools
        // see the more useful message. httpStatus: null здесь и значит
        // «api-ru тоже не ответил» — тот случай, который в тексте ошибки
        // неотличим от «ходили только на api.agapella.io».
        trackApiRuFallback({ phase: 'outcome', errorKind, method, path, httpStatus: null });
        throw err;
      } finally {
        if (retryTimer) clearTimeout(retryTimer);
      }
    }
  };
}

/** Visible-for-testing helper. */
export function _resetApiFallback(): void {
  consecutiveFailures = 0;
  clearSticky();
  clearRuMode(); // keep the shared cross-layer sticky test-isolated too
}
